import React from 'react';
import styles from './QuotationElement.module.css';

/**
 * QuotationElement - Styled Educational Text Box
 * 
 * Displays formatted educational content in a visually distinct quotation box.
 * Useful for highlighting key concepts, definitions, important notes, and learning objectives.
 * 
 * The component supports:
 * - Array of text strings rendered as separate paragraphs
 * - Special '\n' string for line breaks
 * - Optional children for additional content (images, buttons, etc.)
 * - Styled border and background for visual emphasis
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {string[]} [props.texts=[]] - Array of text strings to display as paragraphs
 *                                       Use '\n' as a string element to insert line breaks
 * @param {React.ReactNode} [props.children] - Optional additional content below the text
 * 
 * @returns {JSX.Element} Styled quotation box with formatted text content
 * 
 * @example
 * // Basic quotation with multiple paragraphs
 * <QuotationElement 
 *   texts={[
 *     "Heat naturally flows from hot to cold areas.",
 *     "\n",
 *     "This is the second law of thermodynamics."
 *   ]} 
 * />
 * 
 * @example
 * // Quotation with additional content
 * <QuotationElement texts={["Key concept: Energy is conserved."]}>
 *   <Button>Learn More</Button>
 * </QuotationElement>
 */
export function QuotationElement({ texts = [], children }) {
  return (
    <div className={styles.container}>
      {/* Text content wrapper: ensure left-aligned text for readability */}
      <div className={styles.textWrapper} style={{ textAlign: 'left' }}>
        {texts.map((text, index) => (
          // Render line break for '\n' strings, otherwise render as paragraph
          text === '\n' ? (
            <br key={index} />
          ) : (
            <p key={index} className={styles.text} style={{ textAlign: 'left' }}>{text}</p>
          )
        ))}
      </div>

      {/* Optional children content area: stack vertically and align left */}
      {children && (
        <div
          className={styles.children}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * QoutationElement - Legacy alias for QuotationElement
 * @deprecated Use QuotationElement instead (correct spelling)
 * Maintained for backwards compatibility with existing code
 */
export const QoutationElement = QuotationElement;

export default QuotationElement;
